import { Document, Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import { getDatesArrByMonth, getDayNameByDate } from "utils/attendanceUtility";

// Create styles
const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    padding: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
    textAlign: "center",
    paddingBottom: 2,
    borderBottomWidth: 2,
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 10,
  },
  // Users Styles
  userDataContainer: {
    width: "95%",
    margin: "0 auto",
    marginBottom: 10,
    gap: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "start",
  },
  userRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 2,
  },
  userSubRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 30,
  },
  userHeadingMain: {
    fontSize: 15,
    fontWeight: "extrabold",
    marginBottom: 2,
  },
  userHeading: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "left",
  },
  userBody: {
    fontSize: 10,
    fontWeight: "semibold",
    textAlign: "right",
  },
  tableContainer: {
    width: "100%",
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    fontSize: 12,
    width: "14%",
    height: 19,
    fontWeight: "extrabold",
    textAlign: "center",
    borderStyle: "solid",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderBottomWidth: 1,
  },
  tableCol: {
    fontSize: 9,
    width: "14%",
    height: 19,
    textAlign: "center",
    borderStyle: "solid",
    justifyContent: "center",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
  },
  tableCellHeader: {
    fontSize: 12,
    padding: 1,
    height: 19,
    paddingLeft: 18,
    borderLeftWidth: 1,
    textAlign: "left",
    fontWeight: "bold",
  },
  tableCell: {
    fontSize: 9,
    height: 19,
    textAlign: "center",
  },
  tableFirstCol: {
    fontSize: 9,
    width: "12.5%",
    borderStyle: "solid",
    borderWidth: 1,
    height: 19,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#d3d3d3",
    textAlign: "left",
  },
  // Summary
  summaryContainer: {
    width: "100%",
    marginTop: 10,
    border: "2px solid #000",
  },
  // Department header
  departmentHeader: {
    fontSize: 16,
    fontWeight: "bold",
    marginTop: 15,
    marginBottom: 5,
    padding: 5,
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
  },
  // Page break
  pageBreak: {
    marginTop: 10,
    marginBottom: 10,
    borderTopWidth: 1,
    borderTopColor: "#ccc",
    borderTopStyle: "dashed",
  },
  noDataMessage: {
    fontSize: 14,
    textAlign: "center",
    marginTop: 40,
    marginBottom: 40,
  },
});

// Create Document Component
export const MonthlyAttendanceAllUsersReport = ({ selectedReport }) => {
  /*   All Functions   ********************************************* */

  // Create a function to convert "18:11:56" to "6:11 PM"
  function formatTime(timeString) {
    if (typeof timeString !== "string") {
      return null;
    }

    // Split the time string into hours, minutes, and seconds
    let [hours, minutes, seconds] = timeString?.split(":").map(Number);

    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12; // Handle midnight (0) and 12 PM
    const formattedMinutes = minutes.toString().padStart(2, "0");
    return `${formattedHours}:${formattedMinutes} ${period}`;
  }

  // Check if we have valid data
  if (
    !selectedReport ||
    !selectedReport.usersReports ||
    selectedReport.usersReports.length === 0
  ) {
    return (
      <Document title={"Monthly Attendance Report"}>
        <Page style={styles.page}>
          <View style={styles.title}>
            <Text>Monthly Attendance Report</Text>
          </View>
          <View style={styles.noDataMessage}>
            <Text>No data available for the selected period</Text>
          </View>
        </Page>
      </Document>
    );
  }

  // Group users by department
  const groupUsersByDepartment = () => {
    const departmentGroups = {};

    selectedReport.usersReports.forEach((userReport) => {
      const department = userReport.userData.department || "Unassigned";

      if (!departmentGroups[department]) {
        departmentGroups[department] = [];
      }

      departmentGroups[department].push(userReport);
    });

    return departmentGroups;
  };

  const renderUserAttendanceTable = (userReport) => {
    const monthYear = userReport.monthYear;

    const DatesList = getDatesArrByMonth(monthYear);
    const headings = [
      "Date",
      "In Time",
      "Out Time",
      "Late Time",
      "Over Time",
      "Total Hours",
    ];

    return (
      <View>
        <View style={styles.userDataContainer}>
          <View style={{ width: "40%" }}>
            <Text style={[styles.userHeadingMain, { marginBottom: 5 }]}>
              {userReport.userData.name}
            </Text>
            <View style={styles.userSubRow}>
              <Text style={styles.userHeading}>Employee ID:</Text>
              <Text style={styles.userBody}>
                {userReport.userData.userId || userReport.userData.uid}
              </Text>
            </View>
          </View>
          <View style={{ width: "40%" }}>
            <Text style={[styles.userHeadingMain, { marginBottom: 5 }]}>
              {selectedReport.monthNameDate} {selectedReport.year}
            </Text>
            <View style={styles.userRow}>
              <Text style={styles.userHeading}>Badge No</Text>
              <Text style={styles.userBody}>
                {userReport.userData.badgeNo || "N/A"}
              </Text>
            </View>
          </View>
        </View>

        {/* Attendance Records */}
        <View style={[styles.tableContainer]}>
          <View style={styles.tableRow}>
            {headings?.map((heading, i) => (
              <View
                key={heading}
                style={[
                  styles.tableColHeader,
                  {
                    borderLeftWidth: i === 0 ? 1 : 0,
                    width: i === 0 ? "30%" : "14%",
                  },
                ]}
              >
                <Text style={styles.tableCellHeader}>{heading}</Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.tableContainer}>
          {DatesList.map((date, index) => {
            const record = userReport.attendanceRecords[date] || {};
            const dayNameOfWeek =
              record.dayNameOfWeek || getDayNameByDate(date);
            const isSunday = dayNameOfWeek === "Sunday";
            const isAbsent = record.message === "Absent";
            const isHoliday =
              record.type === "Holiday" || record.message === "Holiday";

            return (
              <View
                key={index}
                style={[
                  styles.tableRow,
                  { backgroundColor: isSunday ? "#d3d3d3" : "white" },
                ]}
              >
                {/* Date column */}
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text style={[styles.tableCellHeader]}>
                    {date} ({dayNameOfWeek})
                    {isHoliday && (
                      <Text style={{ fontStyle: "italic", color: "red" }}>
                        {" "}
                        (H)
                      </Text>
                    )}
                  </Text>
                </View>

                {/* In Time column */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        color: isAbsent ? "red" : isHoliday ? "green" : "black",
                      },
                    ]}
                  >
                    {isAbsent ? "Absent" : formatTime(record.inTime) || "-"}
                  </Text>
                </View>

                {/* Out Time column */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        color: isAbsent ? "red" : isHoliday ? "green" : "black",
                      },
                    ]}
                  >
                    {isAbsent ? "Absent" : formatTime(record.outTime) || "-"}
                  </Text>
                </View>

                {/* Late Time column */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      {
                        color: isAbsent ? "red" : isHoliday ? "green" : "black",
                      },
                    ]}
                  >
                    {isAbsent ? "Absent" : record.totalLateTime || "-"}
                  </Text>
                </View>

                {/* Over Time column */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      { color: isAbsent ? "red" : "black" },
                    ]}
                  >
                    {isAbsent ? "Absent" : record.overTime || "-"}
                  </Text>
                </View>

                {/* Total Hours column */}
                <View style={[styles.tableCol]}>
                  <Text
                    style={[
                      styles.tableCell,
                      { color: isAbsent ? "red" : "black" },
                    ]}
                  >
                    {isAbsent ? "Absent" : record.totalHours || "-"}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>

        {/* Summary */}
        <View>
          <View style={styles.summaryContainer}>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 10,
                paddingVertical: 5,
              }}
            >
              <View style={{ width: "50%" }}>
                <Text style={styles.userHeading}>Total Late Time</Text>
              </View>
              <View style={{ width: "50%" }}>
                <Text style={styles.userBody}>
                  {userReport.summaryData?.totalLateTime || "0.0"}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 10,
                paddingVertical: 5,
              }}
            >
              <View style={{ width: "50%" }}>
                <Text style={styles.userHeading}>Absent Days</Text>
              </View>
              <View style={{ width: "50%" }}>
                <Text style={styles.userBody}>
                  {userReport.summaryData?.absentDaysCount || 0}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 10,
                paddingVertical: 5,
              }}
            >
              <View style={{ width: "50%" }}>
                <Text style={styles.userHeading}>Total Over Time</Text>
              </View>
              <View style={{ width: "50%" }}>
                <Text style={styles.userBody}>
                  {userReport.summaryData?.totalOverTime || "0.0"}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                paddingHorizontal: 10,
                paddingVertical: 5,
              }}
            >
              <View style={{ width: "50%" }}>
                <Text style={styles.userHeading}>Total Working Hours</Text>
              </View>
              <View style={{ width: "50%" }}>
                <Text style={styles.userBody}>
                  {userReport.summaryData?.totalWorkingHours || "0.0"}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const departmentGroups = groupUsersByDepartment();

  return (
    <Document title={"Monthly Attendance Report"}>
      {Object.entries(departmentGroups).map(([department, users]) =>
        users.map((userReport, userIndex) => (
          <Page
            key={`${department}-${userReport.userData.uid}-${userIndex}`}
            style={styles.page}
          >
            {renderUserAttendanceTable(userReport)}
          </Page>
        ))
      )}
    </Document>
  );
};
